import {
  Component,
  effect,
  EventEmitter,
  Input,
  Output,
  signal,
} from '@angular/core';
import { ComponentUtilsService } from '@core/services';
import {
  customComponentListener,
  formConfig,
  onFormFieldChangeType,
} from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import { CustomSelectBoxComponent } from '@shared/components';
import { CustomTextInputComponent } from '@shared/components/text-input/text-input.component';

export type onFormEventType = { data: any };

@Component({
  selector: 'form-component',
  templateUrl: 'form.component.html',
  imports: [
    TranslateModule,
    CustomSelectBoxComponent,
    CustomTextInputComponent,
  ],
})
export class CustomFormComponent {
  @Input({ required: true }) config!: formConfig;
  @Input({ required: false }) listeners!: customComponentListener[];
  @Input() watchFields?: string[];

  @Output() onWatchFieldsChange = new EventEmitter<onFormFieldChangeType>();

  @Output() onFormChange: EventEmitter<onFormEventType> =
    new EventEmitter<onFormEventType>();

  @Output() onFormCompleted: EventEmitter<onFormEventType> =
    new EventEmitter<onFormEventType>();

  private lastWatchedValues = signal<{ [key: string]: any }>({});

  @Input({ required: false }) formData = signal<{ [key: string]: any }>({});

  protected fieldSpan = {
    1: '3',
    2: '6',
    3: '9',
    4: '12',
  };

  protected formFieldsListener: customComponentListener[] = [
    {
      event: 'onFormFieldChange',
      action: ({ field, value }: onFormFieldChangeType) =>
        this.formData.update((prev) => ({ ...prev, [field]: value })),
    },
  ];

  public patchFieldValue(fieldName: string, value: any): void {
    this.formData.update((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }

  public patchFormData(data: any): void {
    this.formData.update(() => data);
  }

  constructor(private utils: ComponentUtilsService) {
    effect(() => {
      this.onFormChange.emit({ data: this.formData() });

      const requiredFields = this.config.fields
        .filter((f) => f.config.required)
        .map((f) => f.config.fieldName);

      const missingFields = requiredFields.filter(
        (f) => this.formData()[f] == undefined
      );

      if (missingFields.length > 0) return;

      this.onFormCompleted.emit({ data: this.formData() });
    });

    effect(() => {
      if (!this.watchFields || this.watchFields.length === 0) return;

      const current = this.formData();
      const previous = this.lastWatchedValues();
      const changedValues: { [key: string]: any } = {};

      for (const key of this.watchFields) {
        const prev = previous[key];
        const curr = current[key];

        if (curr !== prev) {
          changedValues[key] = curr;
        }
      }

      if (Object.keys(changedValues).length > 0) {
        this.lastWatchedValues.update(() => ({
          ...previous,
          ...changedValues,
        }));

        this.onWatchFieldsChange.emit({
          field: Object.keys(changedValues)[0],
          value: Object.values(changedValues)[0],
        });
      }
    });
  }

  ngOnInit() {
    this.utils.attachListeners(this, this.listeners);
  }
}
